import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, ArrowLeft, Calendar, MapPin } from 'lucide-react';

const ManageEvents = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_bn: '',
    description_en: '', description_bn: '',
    location_en: '', location_bn: '',
    date: '', time: '', image_url: '',
  });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch('/api/events/', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setEvents(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ title_en: '', title_bn: '', description_en: '', description_bn: '', location_en: '', location_bn: '', date: '', time: '', image_url: '' });
    setEditingEvent(null);
    setShowForm(false);
  };

  const handleEdit = (event) => {
    setFormData({
      title_en: event.title?.en || '', title_bn: event.title?.bn || '',
      description_en: event.description?.en || '', description_bn: event.description?.bn || '',
      location_en: event.location?.en || '', location_bn: event.location?.bn || '',
      date: event.date?.split('T')[0] || '', time: event.time || '', image_url: event.image_url || '',
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    const body = {
      title: { en: formData.title_en, bn: formData.title_bn },
      description: { en: formData.description_en, bn: formData.description_bn },
      location: { en: formData.location_en, bn: formData.location_bn },
      date: formData.date, time: formData.time, image_url: formData.image_url,
    };
    try {
      const url = editingEvent ? `/api/admin/events/${editingEvent._id}` : '/api/admin/events';
      const method = editingEvent ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      toast({ title: editingEvent ? 'Event updated!' : 'Event created!' });
      resetForm();
      fetchEvents();
    } catch (e) { toast({ title: 'Error saving event', variant: 'destructive' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    const token = localStorage.getItem('admin_token');
    await fetch(`/api/admin/events/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    toast({ title: 'Event deleted!' });
    fetchEvents();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-events-page">
      <div className="bg-party-red text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Manage Events</h1>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-white text-party-red hover:bg-gray-100" data-testid="add-event-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Event
        </Button>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {showForm && (
          <Card className="mb-6 border-t-4 border-t-party-red">
            <CardHeader><CardTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input placeholder="Title (English)" value={formData.title_en} onChange={e => setFormData({...formData, title_en: e.target.value})} required />
                <Input placeholder="Title (Bengali)" value={formData.title_bn} onChange={e => setFormData({...formData, title_bn: e.target.value})} />
                <Textarea placeholder="Description (English)" value={formData.description_en} onChange={e => setFormData({...formData, description_en: e.target.value})} />
                <Textarea placeholder="Description (Bengali)" value={formData.description_bn} onChange={e => setFormData({...formData, description_bn: e.target.value})} />
                <Input placeholder="Location (English)" value={formData.location_en} onChange={e => setFormData({...formData, location_en: e.target.value})} />
                <Input placeholder="Location (Bengali)" value={formData.location_bn} onChange={e => setFormData({...formData, location_bn: e.target.value})} />
                <Input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                <Input placeholder="Time (e.g. 10:00 AM)" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} />
                <Input placeholder="Image URL" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} className="md:col-span-2" />
                <div className="md:col-span-2 flex gap-2">
                  <Button type="submit" className="bg-party-red hover:bg-party-red-600">{editingEvent ? 'Update' : 'Create'}</Button>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div></div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No events found.</div>
        ) : (
          <div className="grid gap-4">
            {events.map((event) => (
              <Card key={event._id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{t(event.title)}</h3>
                    <div className="flex gap-4 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{event.date?.split('T')[0]}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{t(event.location)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(event)} data-testid={`edit-event-${event._id}`}><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(event._id)} data-testid={`delete-event-${event._id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageEvents;
